<script setup lang="ts">
import { useI18n } from 'vue-i18n'
/**
 * MainLayout — authenticated app shell.
 *
 * Owns the persistent chrome (sidebar, topbar, global overlays) and
 * renders the active route in the centre column. Every page that
 * lives inside the workspace uses this layout via nested routes.
 *
 * Responsive behaviour is owned by `Sidebar.vue` + CSS:
 *   ≥ 1513 px → full sidebar (rail + labels)
 *   1025–1512 → icon-only rail, hover-to-peek + click-to-pin
 *   ≤ 1024    → drawer (toggled by `Topbar`'s menu button)
 */
import { onMounted, onBeforeUnmount, watch } from 'vue'
import { RouterView } from 'vue-router'

import Sidebar from '@/components/layout/Sidebar.vue'
import Topbar from '@/components/layout/Topbar.vue'
import Backdrop from '@/components/layout/Backdrop.vue'
import NotificationsPopup from '@/components/layout/NotificationsPopup.vue'
import AccountPopup from '@/components/layout/AccountPopup.vue'
import SearchOverlay from '@/components/layout/SearchOverlay.vue'

import { ToastStack, ConfirmModal, ErrorBoundary, LoadingSkeleton } from '@/components/ui'
import { useUIStore } from '@/stores/ui.store'
import { useAuthStore } from '@/stores/auth.store'
import { useNotificationsStore } from '@/stores/notifications.store'

const { t } = useI18n()

const ui = useUIStore()
const auth = useAuthStore()
const notifications = useNotificationsStore()

/* The bell's live count (W11). The stream belongs to the signed-in workspace:
 * it opens when the layout does and closes when the layout goes — which is
 * what signing out, or a session ending, does — and the bell is emptied so
 * the next person to sign in on this browser starts with their own. */
function stopBell() {
  notifications.reset()
}
watch(
  () => auth.isAuthenticated,
  (signedIn) => {
    if (signedIn) notifications.startStream()
    else stopBell()
  }
)

/* Outside-click closes popups; Escape closes popups + drawers. */
const onDocClick = (e) => {
  if (e.target.closest('.tb-pop, .topbar__action, .user, .search-overlay')) return
  ui.closePopups()
}
const onKey = (e) => {
  if (e.key === 'Escape') {
    ui.closePopups()
    ui.closeDrawers()
    ui.closeSearch()
  }
}
const onResize = () => {
  /* Close drawers when crossing into laptop/desktop range. */
  if (window.innerWidth > 1024) ui.closeDrawers()
}

onMounted(() => {
  if (auth.isAuthenticated) notifications.startStream()
  document.addEventListener('click', onDocClick)
  window.addEventListener('keydown', onKey)
  window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
  stopBell()
  document.removeEventListener('click', onDocClick)
  window.removeEventListener('keydown', onKey)
  window.removeEventListener('resize', onResize)
})
</script>

<template>
  <div class="app">
    <!-- Skip link — first focusable element so keyboard users can
         jump past the sidebar + topbar straight into page content.
         Visually hidden until focused. -->
    <a class="skip-link" href="#main-content">{{ t('common.skipToContent') }}</a>

    <Sidebar />

    <main id="main-content" class="app__main" tabindex="-1">
      <Topbar />
      <!-- Resilience triple-decker:
           1. ErrorBoundary catches Vue runtime errors (render
              throws, watcher failures) and renders ErrorState
              with a Try-again action; the rest of the app
              (sidebar, topbar, popups) stays mounted.
           2. Suspense renders a skeleton while a route chunk
              streams in — covers slow networks and async setup().
           3. RouterView keys on path only so query-string
              changes (filter URL sync) don't remount. -->
      <ErrorBoundary>
        <Suspense>
          <RouterView v-slot="{ Component, route }">
            <component :is="Component" :key="route.path" />
          </RouterView>
          <template #fallback>
            <div class="page">
              <LoadingSkeleton variant="line" :rows="3" />
              <LoadingSkeleton variant="block" height="80px" />
              <LoadingSkeleton variant="row" :rows="6" :cols="5" />
            </div>
          </template>
        </Suspense>
      </ErrorBoundary>
    </main>

    <!-- Global overlays mounted once, like the static prototype. -->
    <NotificationsPopup />
    <AccountPopup />
    <SearchOverlay />
    <ToastStack />
    <ConfirmModal />
    <Backdrop />
  </div>
</template>
